import rawExpectations from "../data/capex-expectations-2026-07-06.json";

export interface CapexExpectationRow {
  year: number;
  companyId: string;
  company: string;
  fiscalLabel: string | null;
  sellside2025_07_15: number;
  sellsideBeforeQ1_2026: number;
  sellside2026_07_06: number;
  buyside2026_07_06: number;
  reportedGapPct: number;
}

export interface CapexExpectationTotal {
  year: number;
  sellside2025_07_15: number;
  sellsideBeforeQ1_2026: number;
  sellside2026_07_06: number;
  buyside2026_07_06: number;
  reportedGapPct: number;
}

export interface CapexExpectationsDataset {
  schemaVersion: number;
  id: string;
  kind: "market-expectations-cross-check";
  asOf: string;
  unit: "USD billions";
  source: {
    status: "user-provided-unverified";
    receivedAt: string;
    originalFileName: string;
    sha256: string;
    attribution: string | null;
    transcription: string;
    warning: string;
  };
  estimates: CapexExpectationRow[];
  totals: CapexExpectationTotal[];
  threeYearTotal: Omit<CapexExpectationTotal, "year"> & { period: string };
}

function closeEnough(left: number, right: number, tolerance = 0.15) {
  return Math.abs(left - right) <= tolerance;
}

export function validateCapexExpectations(input: CapexExpectationsDataset) {
  if (input.kind !== "market-expectations-cross-check") throw new Error("CapEx expectations kind is invalid");
  if (input.source.status !== "user-provided-unverified") throw new Error("CapEx expectations source status must remain explicit");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.asOf)) throw new Error("CapEx expectations asOf must be YYYY-MM-DD");
  if (!/^[a-f0-9]{64}$/.test(input.source.sha256)) throw new Error("CapEx expectations source SHA-256 is invalid");
  if (input.estimates.length !== 15) throw new Error(`Expected 15 company-year CapEx estimates, received ${input.estimates.length}`);
  const expectedYears = [2026, 2027, 2028];
  const expectedCompanies = ["alphabet", "amazon", "meta", "microsoft", "oracle"];
  for (const total of input.totals) {
    const rows = input.estimates.filter((row) => row.year === total.year);
    if (rows.length !== 5) throw new Error(`Expected five companies for ${total.year}, received ${rows.length}`);
    const companies = [...new Set(rows.map((row) => row.companyId))].sort();
    if (JSON.stringify(companies) !== JSON.stringify(expectedCompanies)) throw new Error(`${total.year} company set is invalid`);
    for (const row of rows) {
      const values = [row.sellside2025_07_15, row.sellsideBeforeQ1_2026, row.sellside2026_07_06, row.buyside2026_07_06];
      if (!values.every((value) => Number.isFinite(value) && value >= 0)) throw new Error(`${row.year} ${row.companyId} contains an invalid amount`);
      const derivedGapPct = ((row.buyside2026_07_06 / row.sellside2026_07_06) - 1) * 100;
      if (!closeEnough(derivedGapPct, row.reportedGapPct, 0.75)) throw new Error(`${row.year} ${row.companyId} reported gap does not reconcile`);
    }
    for (const field of ["sellside2025_07_15", "sellsideBeforeQ1_2026", "sellside2026_07_06", "buyside2026_07_06"] as const) {
      const sum = rows.reduce((value, row) => value + row[field], 0);
      if (!closeEnough(sum, total[field])) throw new Error(`${total.year} ${field} total does not reconcile: ${sum} vs ${total[field]}`);
    }
    const derivedTotalGapPct = ((total.buyside2026_07_06 / total.sellside2026_07_06) - 1) * 100;
    if (!closeEnough(derivedTotalGapPct, total.reportedGapPct, 0.75)) throw new Error(`${total.year} total reported gap does not reconcile`);
  }
  const years = [...new Set(input.totals.map((row) => row.year))].sort();
  if (JSON.stringify(years) !== JSON.stringify(expectedYears)) throw new Error("CapEx expectation years are invalid");
  for (const field of ["sellside2025_07_15", "sellsideBeforeQ1_2026", "sellside2026_07_06", "buyside2026_07_06"] as const) {
    const sum = input.totals.reduce((value, total) => value + total[field], 0);
    if (!closeEnough(sum, input.threeYearTotal[field])) throw new Error(`Three-year ${field} total does not reconcile`);
  }
  const derivedThreeYearGapPct = ((input.threeYearTotal.buyside2026_07_06 / input.threeYearTotal.sellside2026_07_06) - 1) * 100;
  if (!closeEnough(derivedThreeYearGapPct, input.threeYearTotal.reportedGapPct, 0.75)) throw new Error("Three-year reported gap does not reconcile");
  return input;
}

export const CAPEX_EXPECTATIONS = validateCapexExpectations(rawExpectations as CapexExpectationsDataset);

export function capexExpectationForYear(year: number) {
  const total = CAPEX_EXPECTATIONS.totals.find((row) => row.year === year);
  if (!total) throw new Error(`CapEx expectations do not include ${year}`);
  return total;
}

export function largestBuysideGaps(year: number, limit = 2) {
  return CAPEX_EXPECTATIONS.estimates
    .filter((row) => row.year === year)
    .sort((left, right) => right.reportedGapPct - left.reportedGapPct)
    .slice(0, limit);
}
